import axios from "axios";
import { toast } from "react-toastify";
import { allNavItems, isItemActive, menuOf } from "../config/navigation";
import { isFullAccessRole } from "../config/roles";

/**
 * Is the page the person is on one their role (as last loaded) includes?
 *
 * A page often loads data from another area's API as well (Product List reads
 * the purchase-order cost window; an order's Manual Tracking form reads the
 * carrier list). A refusal of THAT should not replace the page with Not
 * Allowed: the page is theirs, only the extra is not, and the caller's own
 * error handling decides what to show without it. Only when the page itself
 * is not in the role - or cannot be told - does a refusal mean "not allowed".
 */
const currentPageAllowed = (): boolean => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user) return false;
    if (isFullAccessRole(user.role)) return true;
    const list: string[] = Array.isArray(user.rolePermissions) ? user.rolePermissions : [];
    const here = allNavItems().filter((item) => isItemActive(item, window.location.pathname));
    // A page that is not a navigation entry (an order's page, a shipment's)
    // was already let in by its NavGuard against the same list; a refusal
    // there is of something on it, which the page reports itself.
    if (here.length === 0) return true;
    return here.some((item) => list.includes("*") || list.includes(menuOf(item)));
  } catch {
    return false;
  }
};

/**
 * Opt out of the blanket `success === false` rejection below.
 *
 * That check exists for endpoints where `success: false` really is an error.
 * A few endpoints — the integration connection test above all — return it as a
 * legitimate RESULT with a 200, and the caller needs the body to render it.
 * Without this they get an auto-toast plus a rejected promise, so the component
 * cannot tell "test says no" from "request blew up".
 */
declare module "axios" {
  export interface AxiosRequestConfig {
    skipSuccessCheck?: boolean;
    /** The caller handles a failure itself (e.g. falls back): no error toast. */
    skipErrorToast?: boolean;
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Turn an axios failure into something an admin can act on.
 *
 * The old message was `data.message || error.message`, which meant every failure
 * with no response body surfaced axios's own wording — "Network Error" for an
 * API that is simply not running, "timeout of 0ms exceeded" for a slow one.
 * Neither tells the person reading it what happened or what to do about it.
 *
 * A message the server actually wrote is always preferred: validation errors
 * name the field, and no generic sentence here beats that. Everything else is
 * mapped from the status code, which is the only thing we reliably know.
 */
const friendlyErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const serverMessage = data?.message || data?.msg;

  // The server explained itself — say what it said.
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage;
  }

  // No response at all: the request never reached the API, or timed out.
  if (!error?.response) {
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
      return "The server took too long to respond. Please try again.";
    }
    return "Can't reach the server. Check your connection — the API may not be running.";
  }

  switch (status) {
    case 400:
    case 422:
      return "Some of the details entered are not valid. Please check the form and try again.";
    case 403:
      return "You do not have permission to do this. Contact an administrator if you think you should.";
    case 404:
      return "That item could not be found. It may have been deleted or renamed.";
    case 409:
      return "That conflicts with something that already exists. Refresh and try again.";
    case 413:
      return "That file is too large to upload.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    default:
      if (typeof status === "number" && status >= 500) {
        return "Something went wrong on the server. Please try again in a moment.";
      }
      return "Something went wrong. Please try again.";
  }
};

api.interceptors.response.use(
  (response) => {
    // Check if the API returned a logically failing response with a 200/201 status
    if (
      response.data &&
      response.data.success === false &&
      !response.config?.skipSuccessCheck
    ) {
      const message =
        response.data.message || response.data.msg || "An error occurred";
      toast.error(message);
      return Promise.reject(new Error(message));
    }
    return response;
  },
  (error) => {
    // Ignore canceled/aborted requests (e.g. navigation unmount, AbortController cleanup)
    if (axios.isCancel(error) || error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const message = friendlyErrorMessage(error);

    // The API refused because the user's role does not include this page (as
    // opposed to a single action being refused). The list in the browser is
    // from login time and may be stale, so the app's own guard let them in;
    // the Not Allowed page re-reads it and offers a page they can use.
    // PLAN_FEATURE_REQUIRED: the page is in the role but not in the plan.
    if (
      status === 403 &&
      (error.response?.data?.code === "MENU_NOT_ALLOWED" ||
        error.response?.data?.code === "PLAN_FEATURE_REQUIRED")
    ) {
      if (window.location.pathname !== "/not-allowed" && !currentPageAllowed()) {
        window.location.assign("/not-allowed");
      } else if (window.location.pathname !== "/not-allowed") {
        // Something on an allowed page: leave the page, say why quietly.
        console.warn("Part of this page is not in your role:", error.config?.url, error.response?.data);
      }
      return Promise.reject(error);
    }

    if (status === 401) {
      // Token is invalid/expired. Clear storage and cookies then redirect
      localStorage.removeItem("user");
      const cookieName = import.meta.env.VITE_COOKIE_NAME || "is_logged_in";
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `is_logged_in=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `is_logged_in_prod=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `is_logged_in_staging=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `is_logged_in_dev=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

      if (window.location.pathname !== "/login") {
        toast.error("Session expired. Please log in again.");
        window.location.href = "/login";
      } else {
        toast.error(message || "Invalid credentials");
      }
    } else if (!error.config?.skipErrorToast) {
      toast.error(message);
    }
    return Promise.reject(error);
  },
);

export default api;
